/**
 * @file  src\mobile\commonComponents\ShareWantAd.js
 * @brief  This file is responsible for share want ad functionality
 * @date   April, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import React, { useState, useEffect } from "react";
import Offcanvas from "react-bootstrap/Offcanvas";
import GoogleIcon from "../../../images/g-icon.svg";
import CopyLinkIcon from "../../../images/v2/copy-link-icon.svg";
import EmailIcon from "../../../images/v2/email-icon.svg";
import FacebookIcon from "../../../images/v2/facebook-icon.svg";
import InstagramIcon from "../../../images/v2/instagram-icon.svg";
import LockIcon from "../../../images/v2/lock-icon.svg";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { pageURLs } from "@/constants";
import CopyToClipboard from "react-copy-to-clipboard";
import { EmailShareButton, FacebookShareButton } from "react-share";
import {
  USER_INFO_KEY,
  USER_TYPE_AGENT,
  WANT_AD_KEY,
} from "../../../constants/common";
import {
  getLocalStorage,
  setLocalStorage,
  locationDisplay,
  getUserType,
  getUserId,
} from "../../../helpers/common";
import { shareWantAdSocial } from "../../../services/v2/wantAdService";
import InstaShareModal from "../../../components/Common/v2/InstaShareModal";
import ShareCardPreview from "../../../components/Common/v2/ShareCardPreview";
import { getDescriptionText } from "../../../helpers/v2/getPropertyIcons";
import Loader from "../../../components/Common/Loader";
import {
  getAdTypeTextShare,
  getPropertyTextShare,
} from "../../../helpers/v2/shareWantad";

const ShareWantAd = (props) => {
  const { show, onHide, adDetails } = props;
  const wantAdId = _.get(adDetails, "wantAdId", "");
  const slug = _.get(adDetails, "slug", "");

  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const [shareUrl, setShareUrl] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [fbUrl, setFbUrl] = useState("");
  const userType = getUserType();
  const isAgent = userType === USER_TYPE_AGENT;
  const [showInstaShareModal, setShowIInstaShareModal] = useState(false);
  const currentUserId = getUserId();
  const WantCreatedUserId = _.get(adDetails, "userId", false);

  const generateShareLink = async () => {
    if (!wantAdId || !currentUserId) return;
    try {
      const reqPayload = {
        wantAdId: wantAdId,
        platform: "facebook",
      };
      const response = await shareWantAdSocial(reqPayload);
      setShareUrl(response?.result?.shareUrl || "");
      setFbUrl(response?.result?.facebookShareUrl || "");
      setImageUrl(response?.result?.imageUrl || "");
    } catch (err) {
      throw err;
    }
  };

  useEffect(() => {
    generateShareLink();
  }, [wantAdId]);

  // Open Instagram share modal
  const handleInstagramShare = async () => {
    toast.success("Link copied to clipboard");
    setShowIInstaShareModal(true);
  };

  // Share on Instagram Stories
  const shareOnInstagramStories = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: "Share Want Ad",
          text: loadAdTitle(),
          url: shareUrl,
          files: [await fetchImageAndConvertToFile(imageUrl)],
        });
        toast.success("Shared successfully!");
      } catch (error) {
        // User cancelled or share failed
        console.log("Share cancelled");
        // toast.error("Sharing cancelled");
      }
    } else {
      // Fallback for browsers that don't support Web Share API
      const instagramUrl = `instagram://share?url=${encodeURIComponent(
        shareUrl,
      )}`;
      window.location.href = instagramUrl;
    }
  };
  const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
  const handleFacebookShare = () => {
    const encodedUrl = encodeURIComponent(fbUrl);

    // Different approaches based on platform
    if (isIOS) {
      const facebookAppUrl = `fb://share?link=${encodedUrl}`;

      window.location.href = facebookAppUrl;

      setTimeout(() => {
        fetch(facebookAppUrl, { method: "HEAD", mode: "no-cors" }).catch(() => {
          // If fetch fails, likely the app is not installed
          window.open(
            `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
            "_blank",
          );
        });
      }, 500);
    }
  };

  // Helper function to fetch and convert image to file
  const fetchImageAndConvertToFile = async (imageUrl) => {
    const response = await fetch(imageUrl);
    const blob = await response.blob();
    return new File([blob], "share-image.jpg", { type: "image/jpeg" });
  };

  const loadAdTitle = () => {
    return (
      <>
        <h2 className="long-txt-fix">
          {getAdTypeTextShare(_.get(adDetails, "adType", ""))}{" "}
          {getPropertyTextShare(adDetails)} {locationDisplay(adDetails, true)} |{" "}
          {_.get(adDetails, "adTitle", "")}
        </h2>
      </>
    );
  };

  const handleViewMoreClick = () => {
    const sessionUserInfo = getLocalStorage(USER_INFO_KEY);
    if (pathname === "/mobile/view-listing") {
      onHide();
    } else if (sessionUserInfo?.userInfo?.userId) {
      setLocalStorage(WANT_AD_KEY, {
        wantAdId: wantAdId,
        isFromHomeFeed: true,
      });
      navigate(`${pageURLs.viewAd}/${slug}`);
    }
  };

  return (
    <>
      <Offcanvas
        className="mobile-offcanvas offcanvas-filter-share-fix share-wantad-offcanvas"
        show={show}
        onHide={onHide}
        placement="bottom"
        {...props}
      >
        <Offcanvas.Header closeButton>
          <Offcanvas.Title></Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <div className="offcanvas-sharead-wrap">
            <div className="share-yourad-topsec mb-4 flex-column">
              <div className="share-yourad-main-heading mt-2 mb-2">
                {isAgent
                  ? currentUserId == WantCreatedUserId
                    ? "Expand your sphere 🚀"
                    : "Share this want-listing to your sphere!"
                  : currentUserId == WantCreatedUserId
                    ? "Expand your reach 🚀"
                    : "Share this want-listing to your sphere!"}
              </div>
              {currentUserId == WantCreatedUserId && (
                <div className="share-yourad-text share-yourad-subheading">
                  {isAgent
                    ? "Just like you’d post about your new for-sale listing, tell your network about your new buyer!"
                    : "Share what you’re looking for on social media!"}
                </div>
              )}
            </div>
            <div className="share-yourad-mapwrap-mob">
              <div className="share-yourad-maptop-mob">
                <ShareCardPreview adDetails={adDetails} isSideCard={true} />

                {loadAdTitle()}
                {getDescriptionText(adDetails)}
              </div>

              <div className="share-yourad-mapbtm-mob">
                <a role="button" onClick={handleViewMoreClick}>
                  View more details here
                </a>
                <img src={GoogleIcon} />
              </div>
            </div>

            {!shareUrl ? (
              <Loader loaderText="" />
            ) : (
              <div className="share-yourad-top-mob align-items-start pt-32">
                <CopyToClipboard text={shareUrl}>
                  <a
                    href={void 0}
                    role="button"
                    className="share-btn-wrapper-mob copy-link-mob"
                    onClick={() => {
                      if (shareUrl) {
                        toast.success("Link copied to clipboard");
                      }
                    }}
                  >
                    <div className="share-btn-icon-mob">
                      <img src={CopyLinkIcon} alt="copy-link" />
                    </div>
                    <span className="share-btn-text-mob text-center">
                      Copy Link
                    </span>
                  </a>
                </CopyToClipboard>
                <div className="share-btn-wrapper-mob">
                  <EmailShareButton
                    url={shareUrl}
                    className="share-btn-icon-mob share-email-btn-mob"
                    disabled={!shareUrl}
                  >
                    <img src={EmailIcon} alt="email" />
                  </EmailShareButton>
                </div>

                <div className="share-btn-wrapper-mob ">
                  {!isIOS && (
                    <FacebookShareButton
                      url={fbUrl}
                      className="share-btn-icon-mob  border-green-mob share-fb-btn-mob"
                      disabled={!shareUrl}
                    >
                      <img src={FacebookIcon} alt="facebook" />
                    </FacebookShareButton>
                  )}
                  {isIOS && (
                    <div className="share-btn-wrapper-mob">
                      <a
                        href={void 0}
                        role="button"
                        className="share-btn-icon-mob border-green-mob share-fb-btn-mob"
                        onClick={handleFacebookShare}
                        disabled={!fbUrl}
                      >
                        <img src={FacebookIcon} alt="facebook" />
                      </a>
                    </div>
                  )}
                </div>

                <CopyToClipboard text={shareUrl}>
                  <a
                    href={void 0}
                    role="button"
                    className="share-btn-wrapper-mob"
                    onClick={() => {
                      if (shareUrl) {
                        handleInstagramShare();
                      }
                    }}
                  >
                    <div className="share-btn-icon-mob  border-green-mob share-insta-btn-mob">
                      <img src={InstagramIcon} alt="instagram" />
                    </div>
                  </a>
                </CopyToClipboard>
              </div>
            )}

            {/*
            // TODO: Steps will be functioning onboarding flow changes will be done 
            <div className="share-yourad-bottom-mob">
              <div className="share-yourad-bottom-mob-wrapper">
                <div className="share-yourad-bottom-mob-icon">
                  <img src={LockIcon} alt="lock-icon" />
                </div>
                <div className="share-yourad-bottom-mob-text-container">
                  <h4 className="text-green">
                    Share to Facebook or Instagram to unlock $50 of free direct
                    mail to homeowners!
                  </h4>
                </div>
              </div>
            </div> */}
          </div>
        </Offcanvas.Body>
      </Offcanvas>

      {showInstaShareModal && (
        <InstaShareModal
          show={showInstaShareModal}
          hide={() => setShowIInstaShareModal(false)}
          shareLink={shareUrl}
          fbLink={fbUrl}
          imageLink={imageUrl}
          isMobile={true}
          onShare={shareOnInstagramStories}
        />
      )}
    </>
  );
};
export default ShareWantAd;
